import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SkipForward, CheckCircle } from 'lucide-react';

// Onboarding slide data
const ONBOARDING_SLIDES = [
  {
    id: 'welcome',
    title: 'Welcome to Pulse',
    subtitle: 'Understand how people really feel',
    description: 'Pulse helps gather authentic feedback from stakeholders, analyze perceptions, and drive meaningful and impactful change through data-driven insights.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/c14c7927264a23b274e1c058e9679f3dbc6acbe1d28ac0fb131b059d7c9f5f2c.png',
    color: '#5AA2AE',
  },
  {
    id: 'about-me',
    title: 'About Me',
    subtitle: 'Share your perspective',
    description: 'Answer thoughtful questions about your experience and perceptions. Your responses help build a complete picture of your team\'s health and overall culture.',
    image: 'https://static.prod-images.emergentagent.com/jobs/7976a1f3-d14a-4ebd-8c5e-4b620b34d1b7/images/c3faeea0fdc26bd10f3e8e69d75cae7c5cf1d88a7dfcf6e4b62550a7c9403df7.png',
    color: '#6366f1',
  },
  {
    id: 'about-others',
    title: 'About Others',
    subtitle: 'Provide peer feedback',
    description: 'Share constructive feedback about colleagues and teams. This 360-degree approach creates a comprehensive view of relationships and collaboration.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/ffb2b1a673392bfb3088f5c15d695fb3508b29773723a2c864a8db3a95b0c970.png',
    color: '#8b5cf6',
  },
  {
    id: 'dashboards',
    title: 'Dashboards',
    subtitle: 'See the insights',
    description: 'Explore interactive dashboards that provide an entirely new way to visualize stakeholder perceptions, identify trends, and highlight areas for improvement.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/5880701dc693d94731e27385d9a68c371ab072aa47bad0148af026c4cf74764e.png',
    color: '#0ea5e9',
  },
  {
    id: 'safe-to-speak',
    title: 'Safe to Speak',
    subtitle: 'Your privacy is protected',
    description: 'Your responses are protected by anonymity thresholds and data aggregation. Individual answers are never shared, only collective insights to help the team improve.',
    image: 'https://static.prod-images.emergentagent.com/jobs/794f3022-4e48-459b-970c-fac2a3ecce33/images/5ef9a8264065716287d75bab4c6dcfe21a5c763974cf08843a605781b30d07eb.png',
    color: '#10b981',
    highlights: [
      'Responses are anonymized and aggregated',
      'Individual data is never shared',
      'Minimum response thresholds protect identity',
      'You control what you share',
    ],
  },
];

export default function OnboardingCarousel() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const slide = ONBOARDING_SLIDES[currentSlide];
  const isLastSlide = currentSlide === ONBOARDING_SLIDES.length - 1;
  const isFirstSlide = currentSlide === 0;

  const handleNext = () => {
    if (isLastSlide) {
      handleComplete();
    } else {
      setIsExiting(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide + 1);
        setIsExiting(false);
      }, 200);
    }
  };

  const handlePrevious = () => {
    if (!isFirstSlide) {
      setIsExiting(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide - 1);
        setIsExiting(false);
      }, 200);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    const token = localStorage.getItem('pulse_token');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/auth/invite/${inviteToken}/status`);
      const data = await res.json();
      
      if (data.valid && data.project_id) {
        navigate(`/project/${data.project_id}/about-me`);
        // Trigger user walkthrough after navigation settles
        setTimeout(() => window.dispatchEvent(new CustomEvent('start-user-walkthrough')), 800);
      } else {
        navigate('/projects');
      }
    } catch {
      navigate('/projects');
    }
  };

  const goToSlide = (index) => {
    setIsExiting(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsExiting(false);
    }, 200);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col" data-testid="onboarding-carousel">
      {/* Header */}
      <div className="flex justify-between items-center p-4 md:p-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-[#5AA2AE]">pulse</span>
        </div>
        <Button
          variant="ghost"
          onClick={handleSkip}
          className="text-slate-500 hover:text-slate-700"
          data-testid="skip-onboarding-btn"
        >
          <SkipForward className="w-4 h-4 mr-2" />
          Skip
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-8 pb-8">
        <div 
          className={`w-full max-w-4xl transition-all duration-200 ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Image */}
            <div className="order-2 md:order-1 flex justify-center">
              <div 
                className="relative w-64 h-64 md:w-80 md:h-80 rounded-3xl overflow-hidden"
                style={{ backgroundColor: `${slide.color}10` }}
              >
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-contain p-4"
                />
                {/* Decorative elements */}
                <div 
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20"
                  style={{ backgroundColor: slide.color }}
                />
                <div 
                  className="absolute -bottom-5 -left-5 w-20 h-20 rounded-full opacity-10"
                  style={{ backgroundColor: slide.color }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="order-1 md:order-2 text-center md:text-left">
              <div 
                className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-4"
                style={{ backgroundColor: `${slide.color}15`, color: slide.color }}
              >
                {slide.subtitle}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {slide.title}
              </h1>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {slide.description}
              </p>
              
              {/* Highlights for Safe to Speak */}
              {slide.highlights && (
                <ul className="space-y-3 mb-6">
                  {slide.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-left">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{highlight}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex items-center gap-2 mt-8">
          {ONBOARDING_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide 
                  ? 'w-8 h-2' 
                  : 'w-2 h-2 hover:bg-slate-400'
              }`}
              style={{ 
                backgroundColor: index === currentSlide ? slide.color : '#cbd5e1'
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={isFirstSlide}
            className="gap-2"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <Button
            onClick={handleNext}
            className="gap-2 min-w-[140px]"
            style={{ backgroundColor: slide.color }}
            data-testid="next-slide-btn"
          >
            {isLastSlide ? (
              <>
                Get Started
                <CheckCircle className="w-4 h-4" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Step indicator */}
      <div className="text-center pb-6 text-sm text-slate-400">
        Step {currentSlide + 1} of {ONBOARDING_SLIDES.length}
      </div>
    </div>
  );
}
